import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook that handles FAQ accordion behavior with scroll and deep linking
 * - Toggles FAQ sections open/close
 * - Scrolls to section when manually opened
 * - Auto-opens and scrolls when URL has hash matching FAQ slug
 *
 * @param {Array} data - Array of FAQ items to match against the hash
 * @param {Function} generateSlug - Function that converts an item to a slug string for matching
 * @param {Object} options - Configuration options
 * @param {number} options.scrollOffset - Offset from top in pixels (default: 200)
 * @param {number} options.scrollDelay - Delay before scrolling for hash-based jumps in ms (default: 300)
 * @param {'auto'|'instant'|'smooth'} options.scrollBehavior - Scroll behavior (default: 'smooth')
 * @returns {Object} { openSection, toggleSection, setItem }
 */
export const useFaqScroll = (
  data = [],
  generateSlug = null,
  { scrollOffset = 200, scrollDelay = 300, scrollBehavior = 'smooth' } = {}
) => {
  const [openSection, setOpenSection] = useState(null);
  const item = useRef([]);
  const isInitialMount = useRef(true);

  const toggleSection = (index) => {
    const isOpening = openSection !== index;
    setOpenSection(isOpening ? index : null);

    // Scroll to the section when manually opening it
    if (isOpening && item.current[index]) {
      setTimeout(() => {
        const element = item.current[index];
        if (element) {
          const position = element.getBoundingClientRect().top;
          const offsetTop = position + window.scrollY - scrollOffset;

          window.scrollTo({
            top: offsetTop,
            behavior: scrollBehavior,
          });
        }
      }, 100);
    }
  };

  const setItem = (index) => (element) => {
    item.current[index] = element;
  };

  // Handle URL hash to auto-open and scroll to a specific item
  useEffect(() => {
    if (typeof window === 'undefined' || !data || !Array.isArray(data) || data.length === 0 || !generateSlug) {
      return;
    }

    const hash = window.location.hash.slice(1); // Remove the # symbol
    if (!hash) {
      isInitialMount.current = false;
      return;
    }

    // Find the item index that matches the hash
    const itemIndex = data.findIndex((faqItem) => {
      const slug = generateSlug(faqItem);
      return slug === hash;
    });

    if (itemIndex !== -1) {
      // Open the item
      setOpenSection(itemIndex);

      // Scroll to the item after a delay to ensure the content is fully rendered
      const scrollTimer = setTimeout(() => {
        const element = item.current[itemIndex];
        if (element) {
          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - scrollOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: scrollBehavior,
          });
        }
      }, scrollDelay);

      return () => clearTimeout(scrollTimer);
    }

    isInitialMount.current = false;
  }, [data, generateSlug, scrollOffset, scrollDelay, scrollBehavior]);

  return {
    openSection,
    toggleSection,
    setItem,
  };
};
